import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import { defaultLogRollConfig } from '@tunarr/types';
import type { EverySchedule } from '@tunarr/types/schemas';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { formatBytes } from '../../../helpers/util.ts';
import {
  CheckboxFormController,
  NumericFormControllerText,
} from '../../util/TypedController.tsx';
import type { GeneralSettingsFormData } from './GeneralSettingsForm.tsx';

export const LogRollForm = () => {
  const { t } = useLingui();
  const { control, watch, setValue } =
    useFormContext<GeneralSettingsFormData>();
  const [enabled, schedule] = watch([
    'logging.logRollConfig.enabled',
    'logging.logRollConfig.schedule',
  ]);
  const currentBackupSchedule = schedule as EverySchedule | undefined;

  const handleBackupTimeChange = useCallback(
    (
      value: dayjs.Dayjs | null,
      originalOnChange: (...args: unknown[]) => void,
    ) => {
      if (!value) {
        originalOnChange(0);
        return;
      }

      const h = value.hour();
      const m = value.minute();
      const millis = dayjs.duration({ hours: h, minutes: m }).asMilliseconds();
      originalOnChange(millis);
    },
    [],
  );

  return (
    <Stack gap={2}>
      <FormControl>
        <FormControlLabel
          label={t`Enable Log File Rolling`}
          control={
            <CheckboxFormController
              control={control}
              name="logging.logRollConfig.enabled"
            />
          }
        />
        <FormHelperText>
          <Trans>
            Enable rolling log files using time and/or size based criteria
          </Trans>
        </FormHelperText>
      </FormControl>
      {enabled && (
        <>
          <Grid size={{ xs: 6 }}>
            <FormControl>
              <FormControlLabel
                label={t`Roll on Schedule`}
                control={
                  <Controller
                    control={control}
                    name="logging.logRollConfig.schedule.increment"
                    render={({ field }) => (
                      <Checkbox
                        checked={(field.value ?? 0) > 0}
                        onChange={(_, checked) => {
                          field.onChange(checked ? 1 : 0);
                          setValue(
                            'logging.logRollConfig.schedule.unit',
                            'day',
                            { shouldDirty: true },
                          );
                          setValue(
                            'logging.logRollConfig.schedule.offsetMs',
                            0,
                            { shouldDirty: true },
                          );
                        }}
                      />
                    )}
                  />
                }
              />
              <FormHelperText>
                <Trans>
                  Roll the log file on a fixed schedule, regardless of file
                  size.
                </Trans>
              </FormHelperText>
            </FormControl>
            {(currentBackupSchedule?.increment ?? 0) > 0 && (
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography>
                  <Trans>Every</Trans>
                </Typography>
                <NumericFormControllerText
                  control={control}
                  name="logging.logRollConfig.schedule.increment"
                  prettyFieldName="Max Backups"
                  // The rule outlives this field when it unmounts. Unticking
                  // the schedule writes 0, and min: 1 would block every save.
                  rules={{ min: 0 }}
                  TextFieldProps={{
                    sx: { width: '30%' },
                  }}
                />
                <Controller
                  control={control}
                  name="logging.logRollConfig.schedule.unit"
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={
                        field.value === 'day' || field.value === 'hour'
                          ? field.value
                          : 'day'
                      }
                      sx={{ minWidth: '25%' }}
                    >
                      <MenuItem value="hour">
                        {plural(currentBackupSchedule!.increment, {
                          one: 'Hour',
                          other: 'Hours',
                        })}
                      </MenuItem>
                      <MenuItem value="day">
                        {plural(currentBackupSchedule!.increment, {
                          one: 'Day',
                          other: 'Days',
                        })}
                      </MenuItem>
                    </Select>
                  )}
                />

                {currentBackupSchedule!.unit === 'day' && (
                  <Controller
                    control={control}
                    name="logging.logRollConfig.schedule.offsetMs"
                    render={({ field }) => (
                      <TimePicker
                        value={dayjs()
                          .startOf('day')
                          .add(currentBackupSchedule!.offsetMs)}
                        onChange={(value) =>
                          handleBackupTimeChange(value, field.onChange)
                        }
                      />
                    )}
                  />
                )}
              </Stack>
            )}
          </Grid>
          <Stack direction={'row'}>
            <FormControl fullWidth>
              <FormControlLabel
                label={t`Roll based on size`}
                control={
                  <Controller
                    control={control}
                    name="logging.logRollConfig.maxFileSizeBytes"
                    render={({ field }) => (
                      <Checkbox
                        checked={(field.value ?? 0) > 0}
                        onChange={(_, checked) =>
                          field.onChange(
                            checked
                              ? defaultLogRollConfig().maxFileSizeBytes
                              : 0,
                          )
                        }
                      />
                    )}
                  />
                }
              />
              <FormHelperText>
                <Trans>
                  Roll the log file when it grows past the maximum size.
                </Trans>
              </FormHelperText>
            </FormControl>
            <NumericFormControllerText
              control={control}
              name="logging.logRollConfig.maxFileSizeBytes"
              TextFieldProps={{
                fullWidth: true,
                label: t`Max file size (bytes)`,
                helperText: ({ field }) =>
                  field.value ? `${formatBytes(field.value)}` : '',
              }}
            />
          </Stack>
        </>
      )}
    </Stack>
  );
};
